import { Component, OnInit } from '@angular/core';
import { AppConfigService } from 'src/app/services/app-config/app-config.service';
import * as alertifyjs from 'alertifyjs';

interface RiskFlag {
    code: string;
    category: 'device' | 'network' | 'profile' | 'install';
    label: string;
    description: string;
}

interface CredolabConfigData {
    minScore: number | null;
    maxProbability: number | null;
    requireBoth: boolean | null;
    autoRefuseFlags: Record<string, boolean>;
    availableFlags: RiskFlag[];
    updatedBy: string | null;
    updatedAt: string | null;
}

@Component({
    selector: 'app-credolab-flags',
    templateUrl: './credolab-flags.component.html',
    styleUrls: ['./credolab-flags.component.css']
})
export class CredolabFlagsComponent implements OnInit {

    credolabData: CredolabConfigData | null = null;

    credolabLoading = false;
    credolabNotSeeded = false;
    flagSaving: Record<string, boolean> = {};

    readonly CATEGORY_LABELS: Record<string, string> = {
        device:  'Dispositivo',
        network: 'Red / ubicación',
        profile: 'Perfil',
        install: 'Origen de instalación',
    };

    readonly CATEGORY_ORDER: string[] = ['device', 'network', 'profile', 'install'];

    constructor(private appConfigService: AppConfigService) {}

    ngOnInit(): void {
        this.loadCredolab();
    }

    async loadCredolab(): Promise<void> {
        this.credolabLoading = true;
        try {
            const res = await this.appConfigService.getCredolabConfig();
            this.credolabData = res?.data || res;
            this.credolabNotSeeded = false;
        } catch (error: any) {
            if (error?.status === 404) {
                this.credolabNotSeeded = true;
                this.credolabData = null;
            } else {
                alertifyjs.error(`Error cargando flags: ${error?.error?.message || error?.message || 'desconocido'}`);
            }
        } finally {
            this.credolabLoading = false;
        }
    }

    async toggleFlag(flagKey: string, currentValue: boolean | undefined): Promise<void> {
        const flag = this.credolabData?.availableFlags.find(f => f.code === flagKey);
        const label = flag?.label || flagKey;
        const newValue = !currentValue;
        const confirmed = window.confirm(
            `¿Cambiar flag "${label}" a ${newValue ? 'activado' : 'desactivado'}?`
        );
        if (!confirmed) return;

        this.flagSaving[flagKey] = true;
        try {
            const res = await this.appConfigService.updateCredolabFlag(flagKey, newValue);
            if (res?.data?.unchanged) {
                alertifyjs.message(`El flag "${label}" ya estaba en ese valor.`);
            } else {
                alertifyjs.success(`Flag "${label}" ${newValue ? 'activado' : 'desactivado'}`);
            }
            await this.loadCredolab();
        } catch (error: any) {
            alertifyjs.error(error?.error?.message || error?.message || 'Error al togglear');
        } finally {
            this.flagSaving[flagKey] = false;
        }
    }

    getFlagsByCategory(category: string): RiskFlag[] {
        if (!this.credolabData?.availableFlags) return [];
        return this.credolabData.availableFlags.filter(f => f.category === category);
    }

    isFlagEnabled(code: string): boolean {
        return this.credolabData?.autoRefuseFlags?.[code] === true;
    }

    getCategoryLabel(category: string): string {
        return this.CATEGORY_LABELS[category] || category;
    }

    countEnabledFlagsByCategory(category: string): number {
        return this.getFlagsByCategory(category).filter(f => this.isFlagEnabled(f.code)).length;
    }

    isFlagSaving(flagKey: string): boolean {
        return this.flagSaving[flagKey] === true;
    }
}
